import React, { useState, useCallback, useEffect, memo, useMemo, useRef } from 'react';
import { Pressable, View, FlatList, Platform, TextInput, Alert, Keyboard, TouchableOpacity } from 'react-native';
import { Text, Title } from '@constants/Themed';
import { LinearGradient } from 'expo-linear-gradient';
import styles, { getModernCardStyle, getModernActionStyle, getModernTextColor } from '@stylesheet';
import { FontAwesome5, MaterialIcons } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { ADD_STICKY_NOTE, SHOW_STICKY_NOTES_MODAL, REMOVE_STICKY_NOTE, MARK_NOTE_COMPLETE, SET_STICKY_NOTES, CHANGE_STICKY_NOTE } from '@reduxLocal/actions/types';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apis from '@api/api';
import RenderNote from './Components/RenderNote';
import decreaseBrightness from '@hooks/decreaseBrightness';
import { Tabs, Button, XStack, YStack, Card, H4, Spinner } from 'tamagui';
import ErrorBoundary from '@utils/ErrorBoundary';
import { IndividualNote } from './Components/NoteTypes';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

// Enhanced type definitions
interface StickyNote extends IndividualNote {
  createdAt?: string;
  updatedAt?: string;
  userId?: string;
  isPinned?: boolean;
}

interface NewNoteState {
  _id: string;
  content: string;
  completed: boolean;
  color: string;
}

interface ThemeState {
  lightTheme: boolean;
  primaryColor?: string;
  backgroundColor?: string;
}

// Constants
const STICKY_NOTE_COLORS = ["#ffc09f", "#ffee93", "#fcf5c7", "#a0ced9", "#adf7b6"] as const;
const QUERY_KEYS = {
  NOTES: ['sticky-notes'] as const,
  ACTIVE_NOTES: ['sticky-notes', 'active'] as const,
  COMPLETED_NOTES: ['sticky-notes', 'completed'] as const,
} as const;

// Simple stable TextInput with focus preservation
const StableTextInput = memo(({ 
  initialValue,
  onValueChange, 
  onBlur,
  placeholder, 
  editable, 
  style,
  theme
}: {
  initialValue: string;
  onValueChange: (text: string) => void;
  onBlur?: () => void;
  placeholder: string;
  editable: boolean;
  style: any;
  theme: boolean;
}) => {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<TextInput>(null);
  
  // Update value when initialValue changes from parent (like reset)
  useEffect(() => {
    if (initialValue !== value) {
      setValue(initialValue);
    }
  }, [initialValue]);
  
  const handleChange = useCallback((text: string) => {
    setValue(text);
    onValueChange(text);
  }, [onValueChange]);
  
  return (
    <TextInput
      ref={inputRef}
      value={value}
      onChangeText={handleChange}
      onBlur={onBlur}
      placeholder={placeholder}
      placeholderTextColor={getModernTextColor(theme, 'secondary')}
      editable={editable}
      multiline
      textAlignVertical="top"
      style={{
        borderRadius: 12,
        backgroundColor: 'rgba(255,255,255,0.9)',
        padding: 16,
        fontSize: 15,
        color: getModernTextColor(theme),
        minHeight: 120,
        maxHeight: 200,
        textAlign: 'left',
        lineHeight: 22,
        fontWeight: '400',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.3)',
        ...style,
      }}
      maxLength={500}
    />
  );
});

// Memoized character count component
const MemoizedCharacterCount = memo(({ 
  count, 
  theme 
}: { 
  count: number; 
  theme: boolean; 
}) => (
  <Text style={{ 
    fontSize: 12, 
    color: getModernTextColor(theme, 'muted'),
    fontWeight: '500'
  }}>
    {count}/500
  </Text>
));

// Memoized Notes List Component to prevent re-renders during typing
const MemoizedNotesList = memo(({ 
  currentState, 
  currentNotes, 
  isLoading, 
  isRefetching, 
  error, 
  renderList, 
  refetch, 
  t 
}: {
  currentState: string;
  currentNotes: StickyNote[];
  isLoading: boolean;
  isRefetching: boolean;
  error: any;
    renderList: (notes: StickyNote[], title: string) => React.JSX.Element;
  refetch: () => void;
  t: (key: string) => string;
}) => {
  if (isLoading || isRefetching) {
    return (
      <Card 
        style={{
          ...getModernCardStyle(false, 'medium'),
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          marginHorizontal: 12,
          marginVertical: 8,
          padding: 24,
        }}
      >
        <YStack alignItems="center" space="$4">
          <Spinner size="large" color="$blue10" />
          <H4 color="$color">{t('Loading notes...') || 'Loading notes...'}</H4>
        </YStack>
      </Card>
    );
  }

  if (error) {
    return (
      <Card 
        style={{
          ...getModernCardStyle(false, 'medium'),
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          marginHorizontal: 12,
          marginVertical: 8,
          padding: 32,
        }}
      >
        <YStack alignItems="center" space="$4">
          <MaterialIcons 
            name="error-outline" 
            size={48} 
            color="#f44336" 
          />
          <H4 
            color="#f44336"
            textAlign="center"
            fontSize={18}
            fontWeight="600"
          >
            {t('Failed to load notes') || 'Failed to load notes'}
          </H4>
          <TouchableOpacity
            style={{
              ...getModernActionStyle(false, 'blue'),
              paddingHorizontal: 24,
              paddingVertical: 12,
              borderRadius: 12,
            }}
            onPress={() => refetch()}
          >
            <Text style={{
              color: 'white',
              fontSize: 14,
              fontWeight: '600',
            }}>
              {t('Retry') || 'Retry'}
            </Text>
          </TouchableOpacity>
        </YStack>
      </Card>
    );
  }

  const title = currentState === 'active' ? 'Active Notes' : 'Completed Notes';
  return renderList(currentNotes, title);
});

const StickyNotesComponent = () => {
  // All hooks must be declared at the top level in consistent order
  
  // 1. Redux selectors (these are hooks internally)
  const activeNotes = useSelector((state: DefaultRootState): StickyNote[] => {
    try {
      console.log('Full Redux state:', state);
      console.log('App state:', state?.app);
      console.log('Sticky notes state:', state?.app?.stickyNotes);
      const notes = state?.app?.stickyNotes?.activeNotes;
      console.log('Active notes from Redux:', notes);
      return Array.isArray(notes) ? notes.filter(note => note && typeof note === 'object') : [];
    } catch (error) {
      console.error('Error accessing activeNotes:', error);
      return [];
    }
  });
  
  const completedNotes = useSelector((state: DefaultRootState): StickyNote[] => {
    try {
      const notes = state?.app?.stickyNotes?.completedNotes;
      console.log('Completed notes from Redux:', notes);
      return Array.isArray(notes) ? notes.filter(note => note && typeof note === 'object') : [];
    } catch (error) {
      console.error('Error accessing completedNotes:', error);
      return [];
    }
  });
  
  const theme = themeSelector();

  // 2. Standard hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  
  // 3. State hooks
  const [currentState, setCurrentState] = useState<'active' | 'completed'>('active');
  const [newNote, setNewNote] = useState<NewNoteState>({ 
    _id: '', 
    content: '', 
    completed: false, 
    color: STICKY_NOTE_COLORS[0]
  });

  // 4. Query and mutation hooks
  const { 
    data: notesData, 
    refetch, 
    isLoading, 
    error,
    isRefetching 
  } = useQuery({
    queryKey: QUERY_KEYS.NOTES,
    queryFn: async () => {
      const response = await apis.getStickyNotes();
      return response;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: 3,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });

  const addNoteMutation = useMutation({
    mutationFn: async (noteData: NewNoteState) => {
      const response = await apis.addStickyNote(noteData);
      return response;
    },
    onSuccess: (data, variables) => {
      console.log('Add note success - API response:', data);
      console.log('Add note success - variables:', variables);

      // Update local state
      const noteId = data?.data?.data?._id || data?.data?._id || `temp-${Date.now()}`;
      const newNote = { ...variables, _id: noteId };
      console.log('New note to add to Redux:', newNote);
      
      // Dispatch to Redux
      dispatch({ 
        type: ADD_STICKY_NOTE, 
        payload: newNote
      });
      
      // Reset form
      setNewNote({ 
        _id: '', 
        content: '', 
        completed: false, 
        color: getRandomColor()
      });
      
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTES });
    },
    onError: (error) => {
      console.error('Error adding note:', error);
      Alert.alert(
        t('Error') || 'Error', 
        t('Failed to add note. Please try again.') || 'Failed to add note. Please try again.'
      );
    }
  });

  const updateNoteMutation = useMutation({
    mutationFn: async (noteData: StickyNote) => {
      const response = await apis.modifyStickyNote(noteData);
      return response;
    },
    onSuccess: (data, variables) => {
      // Dispatch to Redux
      dispatch({
        type: CHANGE_STICKY_NOTE,
        payload: variables
      });

      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTES });
    },
    onError: (error) => {
      console.error('Error updating note:', error);
      Alert.alert(
        t('Error') || 'Error',
        t('Failed to update note. Please try again.') || 'Failed to update note. Please try again.'
      );
    }
  });

  const deleteNoteMutation = useMutation({
    mutationFn: async (noteId: string) => {
      const response = await apis.removeStickyNote('', noteId);
      return response;
    },
    onSuccess: (data, variables) => {
      // Dispatch to Redux
      dispatch({
        type: REMOVE_STICKY_NOTE,
        payload: variables
      });

      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTES });
    },
    onError: (error) => {
      console.error('Error deleting note:', error);
      Alert.alert(
        t('Error') || 'Error',
        t('Failed to delete note. Please try again.') || 'Failed to delete note. Please try again.'
      );
    }
  });

  const markCompleteMutation = useMutation({
    mutationFn: async (noteData: StickyNote) => {
      const response = await apis.markNoteComplete('', noteData);
      return response;
    },
    onSuccess: (data, variables) => {
      // Dispatch to Redux
      dispatch({
        type: MARK_NOTE_COMPLETE,
        payload: variables
      });

      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.NOTES });
    },
    onError: (error) => {
      console.error('Error marking note complete:', error);
      Alert.alert(
        t('Error') || 'Error',
        t('Failed to mark note complete. Please try again.') || 'Failed to mark note complete. Please try again.'
      );
    }
  });

  // 5. Callback hooks (stable functions)
  const getRandomColor = useCallback(() => {
    return STICKY_NOTE_COLORS[Math.floor(Math.random() * STICKY_NOTE_COLORS.length)];
  }, []);

  const handleTextChange = useCallback((text: string) => {
    setNewNote(prev => ({ ...prev, content: text }));
  }, []);

  const handleColorChange = useCallback((color: string) => {
    setNewNote(prev => ({ ...prev, color }));
  }, []);

  const handleAddNote = useCallback(async () => {
    if (!newNote?.content?.trim()) {
      Alert.alert(
        t('Error') || 'Error', 
        t('Please enter note content') || 'Please enter note content'
      );
      return;
    }

    // Dismiss keyboard
    Keyboard.dismiss();
    
    // Use mutation instead of direct API call
    addNoteMutation.mutate(newNote);
  }, [newNote, addNoteMutation, t]);

  const handleSubmitEditing = useCallback(() => {
    if (newNote?.content?.trim()) {
      handleAddNote();
    }
  }, [newNote?.content, handleAddNote]);

  const renderNoteItem = useCallback(({ item, index }: { item: StickyNote; index: number }) => {
    if (!item || typeof item !== 'object') {
      return null;
    }
    
    const safeItem: StickyNote = {
      _id: item._id || `temp-${index}`,
      content: item.content || '',
      completed: Boolean(item.completed),
      color: item.color || STICKY_NOTE_COLORS[0],
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      userId: item.userId,
      isPinned: item.isPinned,
      ...item
    };
    
    // Calculate number of columns for proper flex styling
    const numColumns = Platform.OS === 'web' ? 3 : 1;
    const itemWidth = Platform.OS === 'web' ? (100 / numColumns) - (10 / numColumns) : 100;
    
    return (
      <View style={{ 
        width: Platform.OS === 'web' ? `${itemWidth}%` : '100%',
        flexBasis: Platform.OS === 'web' ? `${itemWidth}%` : '100%',
        flexGrow: 0,
        flexShrink: 0,
        marginBottom: Platform.OS !== 'web' ? 12 : 0,
      }}>
        <RenderNote 
          note={safeItem} 
          count={index}
          refetch={refetch}
          isStuck={false}
          inputCallback={() => {}}
        />
      </View>
    );
  }, [refetch]); // Only depend on refetch, not on changing state

  const keyExtractor = useCallback((item: StickyNote, index: number) => {
    if (item && typeof item === 'object' && item._id && typeof item._id === 'string') {
      return item._id;
    }
    return `note-${index}-${Date.now()}`;
  }, []);

  const EmptyStateComponent = useCallback(() => (
    <Card 
      style={{
        ...getModernCardStyle(theme, 'medium'),
        alignItems: 'center',
        justifyContent: 'center',
        marginHorizontal: 12,
        marginVertical: 8,
        padding: 32,
        minHeight: 200,
      }}
    >
      <YStack alignItems="center" space="$4">
        <MaterialIcons 
          name="note-add" 
          size={48} 
          color={getModernTextColor(theme, 'muted')}
        />
        <H4 
          color={getModernTextColor(theme)} 
          textAlign="center"
          fontSize={18}
          fontWeight="600"
        >
          {currentState === 'active' 
            ? (t('No active notes') || 'No active notes')
            : (t('No completed notes') || 'No completed notes')
          }
        </H4>
        <Text style={{ 
          color: getModernTextColor(theme, 'secondary'), 
          textAlign: 'center',
          fontSize: 14,
          lineHeight: 20,
          opacity: 0.8
        }}>
          {currentState === 'active' 
            ? (t('Create your first sticky note above') || 'Create your first sticky note above')
            : (t('Complete some notes to see them here') || 'Complete some notes to see them here')
          }
        </Text>
      </YStack>
    </Card>
  ), [theme, currentState, t]);

  const renderList = useMemo(() => (notes: StickyNote[], title: string) => {
    const safeNotes = Array.isArray(notes) ? notes : [];
    const noteCount = safeNotes.length;
    
    // Calculate number of columns based on platform and screen size
    const numColumns = Platform.OS === 'web' ? 3 : 1;
    
    return (
      <FlatList
        data={safeNotes}
        renderItem={renderNoteItem}
        keyExtractor={keyExtractor}
        numColumns={numColumns}
        key={numColumns} // Force re-render when numColumns changes
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        style={{ flex: 1 }}
        contentContainerStyle={[
          { paddingHorizontal: 10, paddingVertical: 5 },
          safeNotes.length === 0 && { flex: 1, justifyContent: 'center' }
        ]}
        columnWrapperStyle={numColumns > 1 ? { 
          justifyContent: 'space-between',
          gap: 10,
          marginBottom: 10
        } : undefined}
        ListEmptyComponent={EmptyStateComponent}
        ListHeaderComponent={
          Platform.OS === 'web' && safeNotes.length > 0 ? (
            <Card 
              style={{
                ...getModernCardStyle(theme, 'small'),
                marginBottom: 12,
                marginHorizontal: 4,
                padding: 16,
              }}
            >
              <H4 
                textAlign="center" 
                color={getModernTextColor(theme)}
                fontSize={16}
                fontWeight="600"
              >
                {`${title}: ${noteCount}`}
              </H4>
            </Card>
          ) : null
        }
        removeClippedSubviews={true}
        maxToRenderPerBatch={10}
        windowSize={5}
        initialNumToRender={10}
      />
    );
  }, [renderNoteItem, keyExtractor, EmptyStateComponent, theme]);

  const NoteCreationComponent = useMemo(() => {
    const isAddingNote = addNoteMutation.isPending;
    
    return (
      <Card
        style={{
          ...getModernCardStyle(theme, 'medium'),
          backgroundColor: newNote.color,
          marginVertical: 8,
          marginHorizontal: 12,
          minHeight: 280,
          borderWidth: 2,
          borderColor: 'rgba(255,255,255,0.3)',
        }}
      >
        <YStack padding="$4" flex={1}>
        {/* Color Selection Row */}
        <XStack justifyContent="space-between" alignItems="center" marginBottom="$3">
          <Text style={{ 
            fontSize: 14, 
            fontWeight: '600',
            color: getModernTextColor(theme),
            opacity: 0.8 
          }}>
            {t('Choose Color') || 'Choose Color'}
          </Text>
          
          <XStack space="$2">
            {STICKY_NOTE_COLORS.map((color, index) => (
              <TouchableOpacity
                key={index}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  backgroundColor: color,
                  borderWidth: newNote.color === color ? 3 : 2,
                  borderColor: newNote.color === color ? getModernTextColor(theme) : 'rgba(255,255,255,0.6)',
                  shadowOpacity: 0.3,
                  shadowRadius: 4,
                  shadowOffset: { width: 0, height: 2 },
                  elevation: 3,
                  ...(newNote.color === color && {
                    transform: [{ scale: 1.1 }],
                  }),
                }}
                onPress={() => handleColorChange(color)}
                disabled={isAddingNote}
              >
                {newNote.color === color && (
                  <View style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: [{ translateX: -6 }, { translateY: -6 }],
                  }}>
                    <MaterialIcons 
                      name="check" 
                      size={12} 
                      color={getModernTextColor(theme)} 
                    />
                  </View>
                )}
              </TouchableOpacity>
            ))}
          </XStack>
        </XStack>
        
        {/* Main Input Area */}
        <YStack flex={1} space="$3">
          <XStack flex={1} space="$3" alignItems="flex-start">
            <StableTextInput
              key="new-note-input" // Add stable key to prevent unmounting
              initialValue={newNote.content}
              onValueChange={handleTextChange}
              onBlur={handleSubmitEditing}
              placeholder={t('addSticky') || 'Add a new sticky note...'}
              editable={!isAddingNote}
              style={{ flex: 1 }}
              theme={theme}
            />
            
            <TouchableOpacity
              style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  backgroundColor: '#3b82f6',
                  justifyContent: 'center',
                  alignItems: 'center',
                  shadowOpacity: 0.2,
                  shadowRadius: 3,
                  shadowOffset: { width: 0, height: 1 },
                  elevation: 2,
                opacity: isAddingNote || !newNote.content?.trim() ? 0.6 : 1,
              }}
              onPress={handleAddNote}
              disabled={isAddingNote || !newNote.content?.trim()}
            >
              {isAddingNote ? (
                <Spinner size="small" color="white" />
              ) : (
                    <MaterialIcons name="add" size={20} color="white" />
              )}
            </TouchableOpacity>
          </XStack>

          {/* Character count and hints */}
          <XStack justifyContent="space-between" alignItems="center">
            <MemoizedCharacterCount 
              count={newNote.content.length} 
              theme={theme} 
            />
            <Text style={{ 
              fontSize: 11, 
              color: getModernTextColor(theme, 'secondary'),
              fontStyle: 'italic',
              opacity: 0.8
            }}>
              {t('Press enter to save') || 'Press enter to save'}
            </Text>
          </XStack>
        </YStack>
        </YStack>
        
        {/* Bottom gradient */}
        <LinearGradient 
          colors={['transparent', decreaseBrightness(newNote.color || STICKY_NOTE_COLORS[0])]} 
          style={{ 
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 12,
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
            opacity: 0.4,
          }} 
        />
      </Card>
    );
  }, [newNote, handleColorChange, handleAddNote, handleTextChange, handleSubmitEditing, addNoteMutation.isPending, t, theme]);

  // 6. Memo hooks
  const noteStats = useMemo(() => ({
    activeCount: activeNotes.length,
    completedCount: completedNotes.length,
    totalCount: activeNotes.length + completedNotes.length
  }), [activeNotes.length, completedNotes.length]);

  const currentNotes = useMemo(() => {
    return currentState === 'active' ? activeNotes : [...completedNotes].reverse();
  }, [currentState, activeNotes, completedNotes]);

  const getCurrentStateContent = useCallback(() => {
    return (
      <MemoizedNotesList
        currentState={currentState}
        currentNotes={currentNotes}
        isLoading={isLoading}
        isRefetching={isRefetching}
        error={error}
        renderList={renderList}
        refetch={refetch}
        t={t}
      />
    );
  }, [currentState, currentNotes, isLoading, isRefetching, error, renderList, refetch, t]);

  // 7. Effect hooks (must be last and in consistent order)
  useEffect(() => {
    setNewNote(prev => ({ 
      ...prev, 
      color: STICKY_NOTE_COLORS[Math.floor(Math.random() * STICKY_NOTE_COLORS.length)]
    }));
  }, []); // No dependencies - only run once on mount

  // Sync React Query data with Redux
  useEffect(() => {
    console.log('Notes data from API:', notesData);
    if (notesData && Array.isArray(notesData)) {
      console.log('Dispatching SET_STICKY_NOTES with data:', notesData);
      dispatch({ type: SET_STICKY_NOTES, payload: notesData });
    }
  }, [notesData, dispatch]);

  // Main render return
  return (
    <YStack flex={1} paddingHorizontal={30}>
      {/* Header with statistics and navigation */}
      <Card 
        style={{
          ...getModernCardStyle(theme, 'small'),
          marginVertical: 8,
          marginHorizontal: 12,
          padding: 16,
          marginBottom: 12,
        }}
      >
        <XStack justifyContent="space-between" alignItems="center">
          <YStack marginBottom={10}>
            <Text style={{ 
              fontSize: 18, 
              fontWeight: '700',
              color: getModernTextColor(theme),
              marginBottom: 4
            }}>
              {t('Sticky Notes') || 'Sticky Notes'}
            </Text>
            <Text style={{ 
              fontSize: 12, 
              color: getModernTextColor(theme, 'secondary'),
              opacity: 0.8 
            }}>
              {t('Active:', { count: noteStats.activeCount }) || `Active: ${noteStats.activeCount}`} • 
              {' '}
              {t('Completed:', { count: noteStats.completedCount }) || `Completed: ${noteStats.completedCount}`}
            </Text>
          </YStack>
          
          <XStack space="$2">
            <TouchableOpacity
              style={{
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: 12,
                backgroundColor: currentState === 'active' ? '#3b82f6' : '#6b7280',
                justifyContent: 'center',
                alignItems: 'center',
                shadowOpacity: 0.2,
                shadowRadius: 3,
                shadowOffset: { width: 0, height: 1 },
                elevation: 2,
                minWidth: 100,
              }}
              onPress={() => setCurrentState('active')}
            >
              <Text style={{ 
                color: 'white',
                fontSize: 14,
                fontWeight: '600'
              }}>
                {t('Active') || 'Active'}
              </Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={{
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: 12,
                backgroundColor: currentState === 'completed' ? '#10b981' : '#6b7280',
                justifyContent: 'center',
                alignItems: 'center',
                shadowOpacity: 0.2,
                shadowRadius: 3,
                shadowOffset: { width: 0, height: 1 },
                elevation: 2,
                minWidth: 100,
              }}
              onPress={() => setCurrentState('completed')}
            >
              <Text style={{ 
                color: 'white',
                fontSize: 14,
                fontWeight: '600'
              }}>
                {t('Completed') || 'Completed'}
              </Text>
            </TouchableOpacity>
          </XStack>
        </XStack>
      </Card>

      {/* Note Creation Section - Only show for active notes */}
      {currentState === 'active' && NoteCreationComponent}

      {/* Notes List */}
      <YStack flex={1}>
        {getCurrentStateContent()}
      </YStack>
    </YStack>
  );
};

export default memo(StickyNotesComponent);
